/**
 * Virtual GAME CLOCK — a single time source that can run faster than the wall
 * clock so a dev can watch growth, the day/night cycle, and weather play out in
 * seconds instead of hours.
 *
 * Everything time-driven (plant growth from `plantedAt`, the day/night phase, the
 * weather chunk chain, wind) reads `gameNow()` instead of `Date.now()`. Because
 * they all read the SAME accelerated clock, they stay mutually consistent at any
 * speed — the sky, weather, and plants fast-forward together.
 *
 * The clock is a pure affine map of real time:  game = vAnchor + (real − rAnchor)·speed,
 * where `real` is the SERVER-CORRECTED wall clock (Date.now() + serverOffsetMs).
 * Changing speed re-anchors so the current game time is preserved (no jump). At the
 * default 1× with no anchor offset and a synchronised device, gameNow() === Date.now(),
 * so normal play is byte-for-byte identical to using the wall clock directly.
 *
 * SERVER SYNCHRONISATION. Everything that could ever matter to another player — growth,
 * market windows, cooldowns, the save's `savedAt` version stamp — is derived from this
 * clock, so a device whose clock is wrong (or set wrong deliberately) skews all of it.
 * `setServerNow()` re-anchors the clock against the server's own `Date.now()`, sent on
 * every /api/config and every /api/save response; the difference is kept as
 * `serverOffsetMs` and applied inside `gameNow()`. Until the first response arrives the
 * offset is 0 and the device clock is used unchanged — offline play still works, it is
 * just not yet vouched for.
 *
 * The offset and the dev speed override COMPOSE rather than fight: the offset shifts which
 * real instant the map reads from, and a re-anchor preserves the LEAD a fast-forward has
 * banked (game time minus real time) rather than the clock reading itself. So a dev running
 * at 60× keeps every game-hour and keeps running at 60× across a save, while at 1× the
 * clock simply lands on the server's time. Preserving the reading instead would cancel the
 * offset out on the spot — see the note on setServerNow.
 *
 * NOTE: this is a DEV aid. At high speed, timestamps written to the save (e.g. a
 * planting's plantedAt) are in game-time; that's fine and self-consistent because
 * growth is always recomputed as gameNow() − plantedAt.
 */

let speed = 1;
let serverOffsetMs = 0;     // server Date.now() − device Date.now(), 0 until the server has been heard from
let rAnchor = Date.now();   // real time at the last (re)anchor (server-corrected)
let vAnchor = Date.now();   // game time at the last (re)anchor

const listeners = new Set<() => void>();

/** The server-corrected real clock this map is built on. Private: callers want gameNow(). */
function realNow(): number {
  return Date.now() + serverOffsetMs;
}

/** Current GAME timestamp (ms). Use this everywhere instead of Date.now() for any
 *  wall-clock-derived progression (growth, day/night, weather). */
export function gameNow(): number {
  return vAnchor + (realNow() - rAnchor) * speed;
}

/**
 * RE-ANCHOR AGAINST THE SERVER'S CLOCK. `serverNow` is the server's own `Date.now()` at the
 * moment it handled the request; `sentAt` is the device time the request left, when known.
 *
 * Half the round trip is subtracted off because `serverNow` was read somewhere in the middle
 * of the exchange, not when the response landed — without it every sync would bias the offset
 * backwards by one network latency, and a player on a slow link would drift further behind
 * than one on a fast one. It is the cheap half of NTP and it is enough here: the target is
 * "nobody can gain hours by setting their clock forward", not millisecond consensus.
 *
 * WHAT IS PRESERVED IS THE SPEED-UP, NOT THE CLOCK READING. It is tempting to hold `gameNow()`
 * still across the correction so nothing jolts — but that is self-defeating: re-anchoring
 * `vAnchor` to the pre-correction reading cancels the offset out by construction, and at the
 * default 1× `gameNow()` would go on returning `Date.now()` exactly, offset dutifully stored and
 * never applied. The whole point is that the reading MOVES onto the server's clock.
 *
 * So the anchors are rewritten to preserve only the accumulated ACCELERATION — how far a dev's
 * fast-forward has pushed game time beyond real time. At 1× with no fast-forward that lead is 0
 * and `gameNow()` becomes server time exactly; at 60× the session keeps every game-hour it has
 * banked and simply advances from a corrected reference. A correction is a step of the offset
 * (typically a few ms after the first sync), which is the intended behaviour: the device clock
 * was wrong and the game's time is being fixed.
 */
export function setServerNow(serverNow: number, sentAt?: number): void {
  if (!Number.isFinite(serverNow) || serverNow <= 0) return;
  const received = Date.now();
  const halfTrip = typeof sentAt === "number" && Number.isFinite(sentAt) && received >= sentAt
    ? (received - sentAt) / 2
    : 0;
  // How far fast-forwarding has pushed game time ahead of the real clock, measured on the OLD offset.
  const lead = gameNow() - realNow();
  serverOffsetMs = serverNow + halfTrip - received;
  rAnchor = realNow();
  vAnchor = rAnchor + lead;
}

/** The current server-vs-device clock skew in ms (0 until a server response has been seen).
 *  Exposed for the smoke test and for diagnostics; gameplay reads gameNow(). */
export function serverOffset(): number { return serverOffsetMs; }

/** Current speed multiplier (1 = real time). */
export function gameSpeed(): number { return speed; }

/** Set the speed multiplier, re-anchoring so game time is continuous. */
export function setGameSpeed(mult: number): void {
  const m = Math.max(1, Math.min(100, mult));
  if (m === speed) return;
  // re-anchor at the current instant so the clock doesn't jump when the rate changes
  const real = realNow();
  vAnchor = vAnchor + (real - rAnchor) * speed;
  rAnchor = real;
  speed = m;
  listeners.forEach((l) => l());
}

/** Subscribe to speed changes (for UI mirroring the current value). */
export function onGameSpeedChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
